import { create } from 'zustand';
import { invalidate } from '@react-three/fiber';

// Define bounds
const X_BOUNDS = [-200, 200];
const Z_BOUNDS = [-200, 200];

// Spring configuration
const SPRING_STRENGTH = 0.12; // Higher = faster movement (0.1-0.3 is good range)
const SPRING_DAMPING = 0.6;  // Higher = less oscillation (0.6-0.9 is good range)
const SPRING_PRECISION = 0.01; // Stop spring when values are close enough

// Animation frame tracking
let animationFrameId = null;

// Throttling for ThreeJS updates
let lastUpdateTime = 0;
const UPDATE_THROTTLE = 16; // ~60fps (16ms between updates)

// Create a store to manage lighting state
export const useLightingStore = create((set, get) => ({
  // Raw input values from UI (target values)
  rawValues: {
    x: 41, // Default value
    y: 0,  // Default value
  },
  
  // Current spring values (actual values with physics)
  currentValues: {
    x: 41,
    y: 0,
  },
  
  // Spring velocity
  velocity: {
    x: 0,
    y: 0
  },
  
  // Processed values for rendering (bounded)
  processedValues: {
    x: 41,
    y: 0,
  },
  
  // Dragging state - disables spring animation during drag
  isDragging: false,
  
  // Set dragging state
  setDragging: (dragging) => {
    set({ isDragging: dragging });
    
    if (dragging) {
      // Stop spring animation when dragging starts
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    } else {
      // Resume spring animation when dragging ends (only if not animating)
      if (!animationFrameId && !window.mattoState?.getState?.()?.doAnimate) {
        startSpringAnimation();
      }
    }
  },
  
  // Set raw values from UI
  setRawValues: (x, y) => {
    // Apply bounds to raw values
    const boundedX = Math.max(X_BOUNDS[0], Math.min(X_BOUNDS[1], x));
    const boundedY = Math.max(Z_BOUNDS[0], Math.min(Z_BOUNDS[1], y));
    
    const { isDragging } = get();
    
    set({ 
      rawValues: { x: boundedX, y: boundedY }
    });
    
    // If we're dragging or animating, update position directly without spring physics
    if (isDragging || window.mattoState?.getState?.()?.doAnimate) {
      set({
        currentValues: { x: boundedX, y: boundedY },
        processedValues: { x: boundedX, y: boundedY },
        velocity: { x: 0, y: 0 }
      });
      
      // Update ThreeJS light immediately during drag or animation
      updateThreeJsLight();
    } else {
      // Start spring animation if not already running and not dragging/animating
      if (!animationFrameId) {
        startSpringAnimation();
      }
    }
  },
  
  // Update current values based on spring physics
  updateSpring: () => {
    const { rawValues, currentValues, velocity, isDragging } = get();
    
    // Don't update spring physics while dragging
    if (isDragging) {
      return false;
    }
    
    // Calculate spring forces
    const deltaX = rawValues.x - currentValues.x;
    const deltaY = rawValues.y - currentValues.y;
    
    // Apply spring formula with damping
    const accelerationX = deltaX * SPRING_STRENGTH;
    const accelerationY = deltaY * SPRING_STRENGTH;
    
    // Update velocity with damping
    const newVelocityX = velocity.x * SPRING_DAMPING + accelerationX;
    const newVelocityY = velocity.y * SPRING_DAMPING + accelerationY;
    
    // Update position
    const newX = currentValues.x + newVelocityX;
    const newY = currentValues.y + newVelocityY;
    
    // Check if we're close enough to stop the animation
    const isSettledX = Math.abs(deltaX) < SPRING_PRECISION && Math.abs(newVelocityX) < SPRING_PRECISION;
    const isSettledY = Math.abs(deltaY) < SPRING_PRECISION && Math.abs(newVelocityY) < SPRING_PRECISION;
    
    // Apply bounds to the processed values
    const processedX = Math.max(X_BOUNDS[0], Math.min(X_BOUNDS[1], newX));
    const processedY = Math.max(Z_BOUNDS[0], Math.min(Z_BOUNDS[1], newY));
    
    // Update state
    set({
      currentValues: {
        x: newX,
        y: newY
      },
      velocity: {
        x: newVelocityX,
        y: newVelocityY
      },
      processedValues: {
        x: processedX,
        y: processedY
      }
    });
    
    // Return whether animation should continue
    return !(isSettledX && isSettledY);
  },
  
  // Get processed values for Three.js
  getProcessedValues: () => {
    const { processedValues } = get();
    return {
      x: processedValues.x,
      y: -processedValues.y // Note: Y is inverted for correct direction
    };
  }
}));

// Function to start spring animation
function startSpringAnimation() {
  if (animationFrameId) return;
  
  const animateSpring = () => {
    // Update spring physics
    const shouldContinue = useLightingStore.getState().updateSpring();
    
    // Update the ThreeJS light
    updateThreeJsLight();
    
    // Request next frame if needed
    if (shouldContinue) {
      animationFrameId = requestAnimationFrame(animateSpring);
    } else {
      animationFrameId = null;
    }
  };
  
  // Start animation loop
  animationFrameId = requestAnimationFrame(animateSpring);
}

// Cleanup function
export function cleanupLightingService() {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}

// Utility function to update lighting in Three.js
export const updateThreeJsLight = () => {
  const now = Date.now();
  
  // Throttle updates during dragging for smoother performance
  if (now - lastUpdateTime < UPDATE_THROTTLE) {
    return;
  }
  lastUpdateTime = now;
  
  // Try to use the direct reference first (more reliable)
  const light = window.directionalLightRef || window.scene?.children?.find((c) => c.name === 'directional light');
  
  if (light?.position) {
    const { x, y } = useLightingStore.getState().getProcessedValues();
    light.position.x = x;
    light.position.z = y; // In Three.js, we use z instead of y for forward/back
    invalidate(); // Trigger a render update
  }
}; 